<template>
<!-- Zwei Spalten nach Layoutskizze B: links die Arbeit (Verlauf, dein
     Spruch, Rueckmeldung), rechts die Bausteine. Schmal untereinander. -->
<div class="reagieren reagieren--zweispaltig">
    <div class="reagieren__arbeit">
    <AblaufKopf :uebung="uebung" :ich="ich" />


    <!-- Was bisher gesagt wurde — auch falsche Sprueche und was die
         Gegenstation darauf tat. Wie im Behelf: jede Station mit eigenem
         Einzug. Die eigenen Sprueche sind hervorgehoben. -->
    <div v-for="e in verlauf" :key="e.nr" class="gesagt" :class="{ eigen: e.spricht === ich }" :style="einzug(e)">
        <p class="station nur-vorlesen">{{ e.station.rufname }}<template v-if="e.spricht === ich"> (du)</template></p>
        <p class="spruch" :class="{ still: e.still }">{{ e.text }}</p>
    </div>

    <template v-if="!geloest">
        <h2>Dein Spruch</h2>
        <p v-if="zeile.length === 0" class="leer">
            Tippe Redewendungen und Inhalt an, in der Reihenfolge, in der du sprichst.
        </p>
        <TransitionGroup v-else tag="ol" name="baustein" class="zeile" :class="{ daneben: falsch }">
            <li v-for="(text, i) in zeile" :key="i">{{ text }}</li>
        </TransitionGroup>

    </template>

    <div v-if="geloest || falsch" class="rueckmeldung" :class="geloest ? 'stimmt' : 'daneben'">
        <p class="satz">{{ geloest ? 'Das sitzt.' : 'Das stimmt noch nicht.' }}</p>
    </div>
    </div>

    <aside class="reagieren__bausteine" aria-label="Lage und Bausteine">
        <!-- Die Lage steht oben in dieser Spalte und bleibt dort stehen,
             waehrend die Bausteine darunter scrollen. -->
        <p class="lage"><span class="lage__etikett">Lage</span>{{ uebung.kontext }}</p>

        <template v-if="!geloest">
        <!-- Redewendungen in Gruppen nach Funktion, ohne Ueberschrift: nur
             der Abstand trennt sie (siehe Redewendungen.js). -->
        <h2>Redewendungen</h2>
        <div class="gruppen">
            <div v-for="(g, i) in redewendungsGruppen" :key="i" class="palette">
                <button v-for="r in g" :key="r" @click="anhaengen(r)">{{ r }}</button>
                <button v-if="i === redewendungsGruppen.length - 1" class="nichts" @click="nichtsSagen()">{{ NICHTS }}</button>
            </div>
        </div>

        <!-- Inhalt nach Station, wie die Spalten im Behelf: pro Station ihre
             Formen, dann die Meldung. Verraet keine Rolle (Adressat …). -->
        <h2>Inhalt</h2>
        <div class="gruppen">
            <div v-for="g in inhaltGruppen" :key="g.titel" class="inhaltsgruppe">
                <p class="inhaltsgruppe__titel">{{ g.titel }}</p>
                <div class="palette">
                    <button v-for="t in g.teile" :key="t" @click="anhaengen(t)">{{ t }}</button>
                </div>
            </div>
        </div>
        </template>
    </aside>

    <div class="knopfleiste">
        <button class="haupt" @click="senden()" :disabled="zeile.length === 0 || geloest">Senden</button>
        <button @click="rueckgaengig()" :disabled="zeile.length === 0 || geloest">Rückgängig</button>
        <slot name="rechts" />
    </div>

</div>
</template>

<script>
import { ref, computed } from 'vue';
import AblaufKopf from '@/components/AblaufKopf.vue';
import { einzugFuer } from '@/logic/Einzug';
import { redewendungen, redewendungsGruppen } from '@/data/Redewendungen';

// Steht in der Zeile, wenn der Lernende bewusst nichts sagt (auf Empfang
// bleiben). Ein Zug mit "stumm" verlangt genau das.
const NICHTS = 'nichts sagen'

// Uebungsart "Reagieren": der Lernende spricht fuer eine Station (uebung.
// reagieren.ich), die App fuer die anderen. Die Zuege der App erscheinen von
// selbst; ist der Lernende dran, baut er seinen Spruch aus zwei Paletten:
//   Redewendungen — fest, immer gleich (data/Redewendungen.js)
//   Inhalt        — Rufnamen ("X", "An X", "von X") und was in den eigenen
//                   Zuegen vorkommt (Meldung, Kanal …) — alphabetisch.
// Die Uebung kommt aus bereiteReagierenVor() (logic/Reagieren.js).
//
// Ein falscher Spruch wird gesprochen, und die Gegenstation reagiert wie im
// echten Funk (Entscheid des Nutzers, 2026-10-16) — siehe folgeAuf().
// Nach zwei solchen Folgen im selben Zug gibt es stattdessen die
// Rueckmeldung, sonst kommt nicht weiter, wer den Fehler nicht sieht.
//
// Vertrag mit dem Container wie bei jeder Uebungsart (siehe
// AllesSortieren.vue). Jedes Senden ist ein Pruefen: falsch zaehlt (auch
// mit Folge), ein richtiger Zwischenspruch nicht, der letzte loest die Uebung.
export default {
    components: { AblaufKopf },
    inheritAttrs: false,
    props: {
        uebung: {type: Object, required: true}
    },
    emits: ['geprueft'],
    setup(props, { emit }) {
        const zuege = props.uebung.zuege
        const ich = props.uebung.reagieren.ich
        const einzug = einzugFuer(props.uebung)
        const meldung = props.uebung.bindung.meldung ? props.uebung.bindung.meldung.funktext : null

        // Was gesprochen wurde, in Reihenfolge: { nr, spricht, station, text, still }
        const verlauf = ref([])
        const sagen = (spricht, station, text, still = false) =>
            verlauf.value.push({ nr: verlauf.value.length, spricht, station, text, still })

        const textVon = (z) => {
            if (z.stumm) return z.spricht === ich ? '(du sagst nichts)' : '(keine Antwort)'
            return z.teile.map(t => z.gestoert && t.rolle === 'inhalt' ? '… (unverständlich) …' : t.text).join(' ')
        }

        // Bis wohin gesprochen ist. Zuege der App laufen gleich durch.
        const pos = ref(0)
        const weiter = () => {
            while (pos.value < zuege.length && zuege[pos.value].spricht !== ich) {
                const z = zuege[pos.value]
                sagen(z.spricht, z.station, textVon(z), z.stumm)
                pos.value += 1
            }
        }
        weiter()

        const geloest = computed(() => pos.value >= zuege.length)

        const zeile = ref([])
        const falsch = ref(false)
        const folgen = ref(0)   // Folgen im aktuellen Zug

        // "X", "An X", "von X" zu jeder Station der Uebung.
        const stationsTexte = new Set()
        for (const s of Object.values(props.uebung.bindung)) {
            if (s && s.rufname) [s.rufname, 'An ' + s.rufname, 'von ' + s.rufname].forEach(t => stationsTexte.add(t))
        }

        // Die Bausteintexte der EIGENEN Zuege (auch gleichwertige Fassungen)
        // und die Rufnamen — ohne die Redewendungen. Nur die eigenen: die
        // falsche Meldung einer Stoerung ("falsch quittiert") stuende sonst
        // schon vorher da und verriete sie.
        const fest = new Set(redewendungen)
        const texte = new Set(stationsTexte)
        for (const z of zuege.filter(z => z.spricht === ich)) {
            for (const t of z.teile) [t.text, ...(t.gleichwertig || [])].forEach(x => texte.add(x))
        }
        const inhalt = [...texte].filter(t => !fest.has(t)).sort((a, b) => a.localeCompare(b, 'de'))

        // Inhalt geordnet: eine Gruppe pro Station (zuerst in der Reihenfolge
        // des Ablaufkopfs), darin An … / Rufname / von …; dann die Meldung,
        // dann der Rest. Ordnet nur um, was schon in "inhalt" steht.
        const stationsNamen = [...new Set([
            ...props.uebung.zuege.map(z => z.station.rufname),
            ...Object.values(props.uebung.bindung).filter(b => b && b.rufname).map(b => b.rufname)
        ])]
        const form = (t) => t.startsWith('An ') ? 0 : t.startsWith('von ') ? 2 : 1
        const inhaltGruppen = []
        for (const name of stationsNamen) {
            const teile = inhalt
                .filter(t => t === name || t === 'An ' + name || t === 'von ' + name)
                .sort((a, b) => form(a) - form(b))
            if (teile.length) inhaltGruppen.push({ titel: name, teile })
        }
        const zugeordnet = new Set(inhaltGruppen.flatMap(g => g.teile))
        const meldungsText = props.uebung.bindung.meldung?.funktext
        const meldungen = inhalt.filter(t => !zugeordnet.has(t) && t === meldungsText)
        const weitere = inhalt.filter(t => !zugeordnet.has(t) && t !== meldungsText)
        if (meldungen.length) inhaltGruppen.push({ titel: 'Meldung', teile: meldungen })
        if (weitere.length) inhaltGruppen.push({ titel: 'Weitere', teile: weitere })

        const anhaengen = (text) => {
            if (zeile.value[0] === NICHTS) zeile.value = []
            zeile.value.push(text)
            falsch.value = false
        }

        const nichtsSagen = () => {
            zeile.value = [NICHTS]
            falsch.value = false
        }

        const rueckgaengig = () => {
            zeile.value.pop()
            falsch.value = false
        }

        const passt = (soll) => soll.stumm
            ? zeile.value.length === 1 && zeile.value[0] === NICHTS
            : zeile.value.length === soll.teile.length &&
              soll.teile.every((t, i) => zeile.value[i] === t.text || (t.gleichwertig || []).includes(zeile.value[i]))

        // Was die Gegenstation auf einen falschen Spruch tut:
        //   nur die Reihenfolge falsch       -> 'rueckmeldung' (verstanden haette
        //                                       man es trotzdem)
        //   falsche oder fehlende Station    -> 'stille' (niemand fuehlt sich gemeint)
        //   Schlusswort fehlt                -> 'stille' (die Gegenstation wartet)
        //   Quittierung mit falschem Inhalt  -> 'falsch' ("falsch ich wiederhole …")
        //   alles andere                     -> 'rueckmeldung'
        // Verglichen wird ueber die Texte, nicht ueber Rollen: die Zuege aus
        // Reagieren.js haben keine.
        const folgeAuf = (soll) => {
            const z = zeile.value
            if (soll.stumm || z[0] === NICHTS) return 'rueckmeldung'
            const sollTexte = soll.teile.map(t => t.text)
            // Gleichwertige Fassung ("verstanden" statt "richtig") wie die Vorgabe zaehlen.
            const gelegt = z.map(x => (soll.teile.find(t => (t.gleichwertig || []).includes(x)) || { text: x }).text)
            const sortiert = (l) => [...l].sort().join('|')
            if (sortiert(gelegt) === sortiert(sollTexte)) return 'rueckmeldung'
            const stationen = (l) => sortiert(l.filter(x => stationsTexte.has(x)))
            if (stationen(z) !== stationen(sollTexte)) return 'stille'
            const schluss = sollTexte[sollTexte.length - 1]
            if ((schluss === 'antworten' || schluss === 'Schluss') && !z.includes(schluss)) return 'stille'
            const quittierung = meldung && sollTexte.includes('verstanden') && sollTexte.includes(meldung) &&
                !sollTexte.some(x => x.includes('wiederhole'))
            if (quittierung && !z.includes(meldung)) return 'falsch'
            return 'rueckmeldung'
        }

        // Wer auf den eigenen Spruch reagiert: die Station, die als naechste
        // spricht (sonst die, die zuletzt sprach).
        const gegenueber = () =>
            zuege.slice(pos.value + 1).find(z => z.spricht !== ich) ||
            zuege.slice(0, pos.value).reverse().find(z => z.spricht !== ich)

        const senden = () => {
            const soll = zuege[pos.value]
            if (passt(soll)) {
                sagen(ich, soll.station, soll.stumm ? '(du sagst nichts)' : zeile.value.join(' '), soll.stumm)
                pos.value += 1
                zeile.value = []
                falsch.value = false
                folgen.value = 0
                weiter()
                emit('geprueft', { sitzt: true, geloest: geloest.value })
                return
            }

            const folge = folgen.value < 2 ? folgeAuf(soll) : 'rueckmeldung'
            if (folge === 'rueckmeldung') {
                falsch.value = true
            } else {
                const g = gegenueber()
                sagen(ich, soll.station, zeile.value.join(' '))
                if (folge === 'stille') sagen(g.spricht, g.station, '(keine Antwort)', true)
                else sagen(g.spricht, g.station, [g.station.rufname, 'falsch ich wiederhole', meldung, 'antworten'].join(' '))
                folgen.value += 1
                zeile.value = []
            }
            emit('geprueft', { sitzt: false, geloest: false })
        }

        return { NICHTS, redewendungen, redewendungsGruppen, inhalt, inhaltGruppen, ich, einzug, verlauf, geloest, zeile, falsch,
                 anhaengen, nichtsSagen, rueckgaengig, senden }
    }
}
</script>

<style scoped>
/* ─── Zwei Spalten (Skizze B) ────────────────────────────────
   Links die Arbeit, rechts die Bausteine; die Knopfleiste ueber beide. */
.reagieren--zweispaltig {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(18rem, 1fr);
    gap: 0 1.75rem;
    align-items: start;
}

.reagieren--zweispaltig .knopfleiste {
    grid-column: 1 / -1;
}

/* Bausteine: hellgrau abgesetzt, kleben beim Scrollen unter der Kopfzeile
   des Modus, damit sie neben dem eigenen Spruch bleiben. */
.reagieren__bausteine {
    position: sticky;
    top: 5rem;
    /* So hoch, dass die Spalte schon ganz oben auf der Seite ueber der
       klebenden Knopfleiste endet (Kopfzeile + Lernzielband + Abstand oben,
       Knopfleiste unten = rund 18.5rem). Lange Listen scrollen in der
       Spalte, die Lage oben bleibt stehen. */
    max-height: calc(100vh - 18.5rem);
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 0 1.1rem 0.25rem;
    background: var(--zs-flaeche);
    border-radius: var(--zs-ecke);
    scrollbar-width: thin;
}

/* Lage oben in der Spalte: bleibt beim Scrollen der Bausteine stehen. */
.lage {
    position: sticky;
    top: 0;
    z-index: 1;
    margin: 0 -1.1rem 1rem;
    padding: 0.85rem 1.1rem;
    font-weight: 600;
    line-height: 1.45;
    color: var(--zs-tinte);
    background: var(--zs-blau-hell);
    border-bottom: 1px solid var(--zs-blau-mittel);
}

.lage__etikett {
    display: block;
    margin-bottom: 0.15rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--zs-blau);
}

/* Gruppen: der Abstand trennt, keine Linie, keine Ueberschrift. */
.gruppen {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    margin-bottom: 1.5rem;
}

.gruppen .palette {
    margin-bottom: 0;
}

.inhaltsgruppe__titel {
    margin: 0 0 0.35rem;
    font-size: 0.78rem;
    font-weight: 650;
    color: var(--zs-blau);
}

.reagieren__bausteine .palette button {
    min-height: 2.25rem;
    padding: 0.35rem 0.65rem;
    font-size: 0.92rem;
    background: var(--zs-tafel);
}

@media (max-width: 900px) {
    .reagieren--zweispaltig {
        grid-template-columns: minmax(0, 1fr);
    }

    .reagieren__bausteine {
        position: static;
        max-height: none;
        overflow: visible;
    }
}

/* Abschnittstitel schmal in Versalien, eine Haarlinie bis zum Rand. */
h2 {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin: 0 0 0.6rem;
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

h2::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--zs-rahmen);
}

.hinweis {
    margin: 0 0 1.75rem;
    font-size: 0.95rem;
    color: var(--zs-grau);
}

/* ─── Verlauf ────────────────────────────────────────────────
   Die eigenen Sprueche hellblau: Blau ist die Farbe deiner Station. */

.gesagt {
    margin: 0 0 0.75rem;
    padding: 0.7rem 0.9rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
}

.gesagt.eigen {
    background: var(--zs-blau-hell);
    border-color: rgba(0, 51, 160, 0.25);
}

/* Station = Farbe Blau, wie ueberall: Farbe steht fuer Station oder fuer
   richtig/falsch, fuer nichts sonst. */
.station {
    margin: 0 0 0.35rem;
    font-size: 0.8rem;
    font-weight: 650;
    color: var(--zs-blau);
}

.spruch {
    margin: 0;
    line-height: 1.45;
}

.spruch.still {
    font-style: italic;
    color: var(--zs-grau);
}

/* ─── Dein Spruch ────────────────────────────────────────────
   Der Rahmen ist immer da und faerbt sich nur bei einem Fehler. */

.zeile {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    min-height: 2.75rem;
    margin: 0 0 1.75rem;
    padding: 0.4rem;
    list-style: none;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    box-sizing: border-box;
}

.zeile.daneben {
    border-color: var(--zs-warnrot);
    box-shadow: 0 0 0 1px var(--zs-warnrot);
}

.zeile li {
    padding: 0.3rem 0.55rem;
    background: var(--zs-flaeche);
    border-radius: var(--zs-ecke);
}

/* ─── Paletten ───────────────────────────────────────────── */

.palette {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-bottom: 1.5rem;
}

.palette button {
    min-height: 2.5rem;
    padding: 0.5rem 0.85rem;
    font: inherit;
    line-height: 1.4;
    color: var(--zs-tinte);
    /* Quelle hellgrau, Ziel (Ablage) weiss: man sieht, was wohin geht. */
    background: var(--zs-flaeche);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s;
}

.palette button:hover {
    background: var(--zs-tafel);
    border-color: var(--zs-tinte);
}

.palette .nichts {
    font-style: italic;
    color: var(--zs-grau);
}

/* ─── Rueckmeldung ───────────────────────────────────────────
   Nie nur Farbe: vor dem Satz steht ein Haken oder ein Kreuz. */

.rueckmeldung {
    margin: 0 0 1rem;
    padding: 0.85rem 1rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
}

.rueckmeldung.stimmt {
    background: var(--zs-oliv-hell);
    border-color: rgba(85, 107, 47, 0.45);
}

.rueckmeldung.daneben {
    background: var(--zs-warnrot-hell);
    border-color: rgba(179, 38, 30, 0.4);
}

.satz {
    margin: 0;
    font-weight: 650;
}

.stimmt .satz {
    color: var(--zs-oliv);
}

.daneben .satz {
    color: var(--zs-warnrot);
}

.stimmt .satz::before {
    content: '\2713\00a0\00a0';
}

.daneben .satz::before {
    content: '\2715\00a0\00a0';
}

.rueckmeldung ul {
    margin: 0.5rem 0 0;
    padding-left: 1.6rem;
}

.rueckmeldung li {
    margin-bottom: 0.2rem;
    line-height: 1.45;
    color: var(--zs-tinte);
}
/* ─── Knopfleiste ────────────────────────────────────────────
   Rechts kommt, was dem Container gehoert (Neue Uebung / Weiter). */

.knopfleiste {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid var(--zs-rahmen);
}

.knopfleiste button {
    min-height: 2.5rem;
    padding: 0.45rem 1rem;
    font: inherit;
    font-weight: 600;
    color: var(--zs-tinte);
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.knopfleiste button:hover:not(:disabled) {
    border-color: var(--zs-tinte);
}

/* Die eigentliche Handlung der Uebung (Pruefen / Senden). */
.knopfleiste .haupt {
    color: #ffffff;
    background: var(--zs-blau);
    border-color: var(--zs-blau);
}

.knopfleiste .haupt:hover:not(:disabled) {
    background: var(--zs-blau-dunkel);
    border-color: var(--zs-blau-dunkel);
}

.knopfleiste button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* Funktext in fester Laufweite: Protokolltext, nicht Bedientext. */
.spruch,
.zeile li,
.palette button {
    font-family: var(--zs-funkschrift);
}

@media (prefers-reduced-motion: reduce) {
    .palette button {
        transition: none;
    }
}
/* ─── Bewegung zeigt, was sich geaendert hat ─────────────────
   Neue Bausteine gleiten kurz herein, umgestellte schieben an ihren
   Platz. Entfernte verschwinden sofort (kein Warten aufs Ausblenden). */
.baustein-enter-active {
    transition: opacity 0.2s ease-out, transform 0.2s ease-out;
}

.baustein-enter-from {
    opacity: 0;
    transform: translateY(6px);
}

.baustein-move {
    transition: transform 0.2s ease-out;
}

/* Leere Ablage: gestrichelter Zielbereich, damit man sieht, wohin die
   Bausteine kommen. */
.leer {
    display: flex;
    align-items: center;
    min-height: 2.75rem;
    margin: 0 0 1.75rem;
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    color: var(--zs-grau);
    background: rgba(255, 255, 255, 0.55);
    border: 1px dashed #9AA3B2;
    border-radius: var(--zs-ecke);
    box-sizing: border-box;
}

@media (prefers-reduced-motion: reduce) {
    .baustein-enter-active,
    .baustein-move {
        transition: none;
    }
}
</style>
